import { useState, useEffect } from 'react';
import { Layout, PageHeader, Container } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { CheckCircle, XCircle, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '../lib/api';

interface CrmIntegration {
  id: string;
  provider: string;
  eurekaProjectId?: string;
  eurekaEntityType?: string;
  logActivity: boolean;
  isActive: boolean;
}

export default function CrmSettings() {
  const [integrations, setIntegrations] = useState<CrmIntegration[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [eurekaConfig, setEurekaConfig] = useState({
    eurekaProjectId: '',
    eurekaEntityType: 'LEAD',
    logActivity: true,
  });

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const data = await api.getCrmIntegrations() as { integrations: CrmIntegration[] };
      setIntegrations(data.integrations || []);
      // Pre-fill form if integration exists
      const existing = data.integrations?.find(i => i.provider === 'EUREKA');
      if (existing) {
        setEurekaConfig({
          eurekaProjectId: existing.eurekaProjectId || '',
          eurekaEntityType: existing.eurekaEntityType || 'LEAD',
          logActivity: existing.logActivity ?? true,
        });
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const saveIntegration = async () => {
    if (!eurekaConfig.eurekaProjectId.trim()) {
      setError('Please enter an Eureka project ID.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await api.createCrmIntegration({
        provider: 'EUREKA',
        eurekaProjectId: eurekaConfig.eurekaProjectId.trim(),
        eurekaEntityType: eurekaConfig.eurekaEntityType,
        logActivity: eurekaConfig.logActivity,
      });

      setSuccess('Eureka CRM integration saved successfully!');
      loadIntegrations();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (id: string) => {
    try {
      setTesting(true);
      setError('');
      setSuccess('');
      const result = await api.testCrmConnection(id) as { success: boolean; projectName?: string; error?: string };
      if (result.success) {
        setSuccess(`Connection successful! Project: ${result.projectName || eurekaConfig.eurekaProjectId}`);
      } else {
        setError(`Connection failed: ${result.error}`);
      }
    } catch (err: any) {
      setError(`Connection test failed: ${err.message}`);
    } finally {
      setTesting(false);
    }
  };

  const deleteIntegration = async (id: string) => {
    try {
      setError('');
      await api.deleteCrmIntegration(id);
      setSuccess('Integration removed.');
      loadIntegrations();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const activeIntegration = integrations.find(i => i.provider === 'EUREKA' && i.isActive);

  return (
    <Layout>
      <PageHeader
        title="CRM Integration"
        description="Connect your scheduling data to Eureka CRM"
      />

      <Container>
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
            <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* Eureka CRM Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Eureka CRM</CardTitle>
                <CardDescription>
                  Log meeting activities to your Eureka project's CRM
                </CardDescription>
              </div>
              {activeIntegration ? (
                <Badge variant="success">Connected</Badge>
              ) : (
                <Badge>Not Connected</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Eureka Project ID
              </label>
              <input
                type="text"
                value={eurekaConfig.eurekaProjectId}
                onChange={(e) => setEurekaConfig({ ...eurekaConfig, eurekaProjectId: e.target.value })}
                placeholder="e.g., cm1a2b3c4d5e6f7g8h9i0j"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in your Eureka project settings or URL bar.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity Type
              </label>
              <select
                value={eurekaConfig.eurekaEntityType}
                onChange={(e) => setEurekaConfig({ ...eurekaConfig, eurekaEntityType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="LEAD">Lead</option>
                <option value="CONTACT">Contact</option>
                <option value="DEAL">Deal</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                What type of CRM entity to create or update when a booking is made.
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="logActivity"
                checked={eurekaConfig.logActivity}
                onChange={(e) => setEurekaConfig({ ...eurekaConfig, logActivity: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="logActivity" className="text-sm text-gray-700">
                Log meeting activity when booking is created
              </label>
            </div>

            <div className="flex space-x-2 pt-4">
              {activeIntegration && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => testConnection(activeIntegration.id)}
                    isLoading={testing}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    Test Connection
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => deleteIntegration(activeIntegration.id)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                  >
                    Remove
                  </Button>
                </>
              )}
              <Button
                onClick={saveIntegration}
                isLoading={saving}
              >
                {activeIntegration ? 'Update Integration' : 'Save Integration'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium">Someone books a meeting</p>
                  <p className="text-sm text-gray-500">Through your scheduling page</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium">Activity is logged in Eureka CRM</p>
                  <p className="text-sm text-gray-500">A MEETING activity is created for the attendee's email</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium">Track meeting history in your CRM</p>
                  <p className="text-sm text-gray-500">See all scheduled meetings alongside your leads, contacts, and deals</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
}
